import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "pp_admin_token";

const adminAxios = axios.create();
adminAxios.interceptors.request.use((config) => {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (t) config.headers["X-Admin-Token"] = t;
  return config;
});

// ---------- Helpers ----------
const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};
const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  } catch { return iso; }
};

// ---------- Login ----------
function AdminLogin({ onAuth }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      onAuth();
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-zinc-50 to-blue-50 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-violet-100 p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold">P</div>
          <div>
            <div className="text-xs uppercase tracking-widest text-violet-600 font-bold">Prime Pillar</div>
            <h1 className="text-2xl font-bold text-zinc-900">Admin Console</h1>
          </div>
        </div>
        <p className="text-sm text-zinc-500 mb-6">Restricted access · session expires in 12 hours.</p>
        <label className="block text-sm font-medium text-zinc-700 mb-2">Admin password</label>
        <div className="relative">
          <input
            type={showPw ? "text" : "password"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            className="w-full pl-4 pr-12 py-3 border border-zinc-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-zinc-900"
            placeholder="Enter password"
            data-testid="admin-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPw(s => !s)}
            tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-violet-600 transition p-1"
            aria-label={showPw ? "Hide password" : "Show password"}
            title={showPw ? "Hide password" : "Show password"}
            data-testid="admin-password-toggle"
          >
            {showPw ? (
              // eye-off icon
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              // eye icon
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
        {err && (
          <div className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="admin-login-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !pw}
          className="mt-6 w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold py-3 rounded-xl hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 transition"
          data-testid="admin-login-submit"
        >
          {loading ? "Authenticating…" : "Enter Admin Console"}
        </button>
        <p className="mt-6 text-xs text-zinc-400 text-center">
          ← <button type="button" onClick={() => { window.history.pushState({}, "", "/"); window.location.reload(); }} className="hover:text-violet-600 underline">Back to app</button>
        </p>
      </form>
    </div>
  );
}

// ---------- Reusable atoms ----------
const Tile = ({ label, value, sub, accent = "violet", testid }) => {
  const accents = {
    violet: "from-violet-500 to-indigo-500",
    emerald: "from-emerald-500 to-teal-500",
    amber: "from-amber-500 to-orange-500",
    rose: "from-rose-500 to-pink-500",
    blue: "from-blue-500 to-cyan-500",
    zinc: "from-zinc-500 to-slate-500",
  };
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5 hover:shadow-md transition" data-testid={testid}>
      <div className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${accents[accent]}`}>
        {label}
      </div>
      <div className="mt-3 text-3xl font-bold text-zinc-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
};

const Section = ({ title, subtitle, children, right }) => (
  <section className="mt-8">
    <div className="flex items-end justify-between mb-3 gap-4">
      <div>
        <h2 className="text-xl font-bold text-zinc-900">{title}</h2>
        {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

// ---------- Sections ----------
function OverviewSection({ data, apify }) {
  if (!data) return <div className="text-zinc-400 text-sm">Loading…</div>;
  const lastScan = data.last_scan;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-overview-tiles">
      <Tile label="Total users" value={fmtNum(data.users.total)} sub={`${data.users.paid} paid · +${data.users.new_this_week} this week`} accent="violet" testid="tile-users" />
      <Tile label="Opportunities" value={fmtNum(data.opportunities.scored)} sub={`${data.opportunities.high_score} ≥70 score · ${data.opportunities.fresh_24h} fresh 24h`} accent="emerald" testid="tile-opps" />
      <Tile label="Raw signals" value={fmtNum(data.raw_signals.total)} sub="Reddit + Fiverr + Upwork + …" accent="blue" testid="tile-raw" />
      <Tile label="MVP briefs" value={fmtNum(data.briefs.total)} sub="Saved across all owners" accent="amber" testid="tile-briefs" />
      <Tile label="Lifetime revenue" value={fmtUsd(data.revenue.lifetime_usd)} sub={`${data.revenue.paid_transactions} paid txns · ${fmtUsd(data.revenue.this_month_usd)} this month`} accent="emerald" testid="tile-revenue" />
      <Tile label="Apify spend (MTD)" value={apify?.configured ? fmtUsd(apify.month_to_date_usd) : "—"} sub={apify?.error ? `⚠ ${apify.error.slice(0,40)}` : (apify?.monthly_limit_usd ? `limit ${fmtUsd(apify.monthly_limit_usd)}` : "month-to-date")} accent="rose" testid="tile-apify" />
      <Tile label="Last scan" value={lastScan ? fmtRelative(lastScan.completed_at || lastScan.started_at) : "—"} sub={lastScan ? `${lastScan.scan_type} · ${lastScan.opportunities_extracted || 0} opps` : "no completed scans"} accent="zinc" testid="tile-lastscan" />
      <Tile label="Paid users" value={fmtNum(data.users.paid)} sub="active founder/pro subscriptions" accent="violet" testid="tile-paid-users" />
    </div>
  );
}

function ScrapingHealthSection({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-scraping-health">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-zinc-500">{data.total_scans} total scans · {data.error_rate_last_30}% error rate (last 30)</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-200">
              <th className="text-left py-2 font-medium">Source</th>
              <th className="text-right py-2 font-medium">Total docs</th>
              <th className="text-right py-2 font-medium">Last 24h</th>
              <th className="text-right py-2 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {data.sources.map((s) => (
              <tr key={s.label} className="border-b border-zinc-100 hover:bg-violet-50/40">
                <td className="py-2 font-medium text-zinc-800">{s.label}</td>
                <td className="text-right py-2">{fmtNum(s.total)}</td>
                <td className="text-right py-2">
                  <span className={s.last_24h > 0 ? "text-emerald-600 font-semibold" : "text-zinc-400"}>{fmtNum(s.last_24h)}</span>
                </td>
                <td className="text-right py-2 text-zinc-500 text-xs">{fmtRelative(s.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2">Recent scan log</div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {data.recent_scans.map((s) => {
            const ok = s.status === "COMPLETED";
            const err = s.status === "FAILED" || s.status === "ERROR";
            return (
              <div key={s.id} className="flex items-center gap-3 text-xs py-1.5 border-b border-zinc-100">
                <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : err ? "bg-rose-500" : "bg-amber-400 animate-pulse"}`}></span>
                <span className="font-mono text-zinc-400">#{s.id}</span>
                <span className="font-semibold text-zinc-700 w-32 truncate">{s.scan_type}</span>
                <span className="text-zinc-500">{s.opportunities_extracted ?? 0} opps · {s.pain_posts ?? 0} pain posts</span>
                <span className="ml-auto text-zinc-400">{fmtRelative(s.started_at)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AiHealthSection({ data }) {
  if (!data) return null;
  const llm = data.llm_health || {};
  const llmOk = llm.status === "healthy";
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="admin-ai-health">
      <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-3">LLM health</div>
        <div className="flex items-center gap-3 mb-3">
          <span className={`inline-block w-3 h-3 rounded-full ${llmOk ? "bg-emerald-500" : "bg-rose-500"}`}></span>
          <span className="font-bold text-zinc-900">{llm.status || "unknown"}</span>
          <span className="text-xs text-zinc-500">last probe {fmtRelative(llm.checked_at)}</span>
        </div>
        <div className="text-sm space-y-1 text-zinc-600">
          <div>Latency: <span className="font-mono">{llm.latency_ms ? `${llm.latency_ms}ms` : "—"}</span></div>
          <div>Message: <span className="text-zinc-700">{llm.message || "—"}</span></div>
          <div>Emergent LLM key configured: <span className={data.emergent_key_configured ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{data.emergent_key_configured ? "yes" : "no"}</span></div>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-3">Score distribution</div>
        <div className="space-y-2">
          {(() => {
            const max = Math.max(1, ...data.score_distribution.map(x => x.count));
            return data.score_distribution.map((b) => {
              const w = (b.count / max) * 100;
              const label = typeof b._id === "number" ? `${b._id}+` : "other";
              return (
                <div key={String(b._id)} className="flex items-center gap-3 text-sm">
                  <div className="w-12 text-zinc-500 font-mono">{label}</div>
                  <div className="flex-1 bg-zinc-100 rounded h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-violet-500 to-emerald-500 h-3 rounded transition-all" style={{width: `${w}%`}}></div>
                  </div>
                  <div className="w-12 text-right font-semibold text-zinc-700">{b.count}</div>
                </div>
              );
            });
          })()}
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-600">
          Deep dive reports generated: <span className="font-semibold">{data.deep_dives.opportunities_with_reports}</span>
          <span className="mx-2 text-zinc-300">·</span>
          users consumed: <span className="font-semibold">{data.deep_dives.users_consumed}</span>
        </div>
      </div>
    </div>
  );
}

function UsersSection({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 overflow-hidden" data-testid="admin-users-table">
      <div className="px-5 py-3 border-b border-zinc-100 text-sm text-zinc-500">{data.total} total users</div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-zinc-500">
              <th className="text-left px-5 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium">Tier</th>
              <th className="text-left px-3 py-2 font-medium">Niches</th>
              <th className="text-right px-3 py-2 font-medium">Deep dives</th>
              <th className="text-right px-3 py-2 font-medium">Expires</th>
              <th className="text-right px-5 py-2 font-medium">Joined</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.email} className="border-b border-zinc-100 hover:bg-violet-50/40">
                <td className="px-5 py-2 font-medium text-zinc-800 max-w-[220px] truncate">{u.email}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                    u.tier === "pro" ? "bg-emerald-100 text-emerald-700" :
                    u.tier === "founder" ? "bg-violet-100 text-violet-700" :
                    "bg-zinc-100 text-zinc-600"
                  }`}>{u.tier}</span>
                </td>
                <td className="px-3 py-2 text-xs text-zinc-500 max-w-[160px] truncate">
                  {(u.niches || []).join(", ") || "—"}
                </td>
                <td className="text-right px-3 py-2 font-mono">{u.deep_dives_used ?? 0}</td>
                <td className="text-right px-3 py-2 text-xs text-zinc-500">{u.subscription_expires_at ? fmtDate(u.subscription_expires_at) : "—"}</td>
                <td className="text-right px-5 py-2 text-xs text-zinc-500">{fmtRelative(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsSection({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-payments">
      <div className="flex flex-wrap gap-3 text-sm mb-4">
        <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{data.totals.paid_count} paid</span>
        <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">{data.totals.open_count} pending</span>
        <span className="px-3 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold">{fmtUsd(data.totals.lifetime_usd)} lifetime</span>
        {Object.entries(data.totals.revenue_by_tier_usd || {}).map(([tier, amt]) => (
          <span key={tier} className="px-3 py-1 rounded-full bg-zinc-100 text-zinc-700 text-xs font-mono">{tier}: {fmtUsd(amt)}</span>
        ))}
      </div>
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-zinc-500">
              <th className="text-left py-2 px-2 font-medium">User</th>
              <th className="text-left py-2 px-2 font-medium">Tier</th>
              <th className="text-right py-2 px-2 font-medium">Amount</th>
              <th className="text-left py-2 px-2 font-medium">Status</th>
              <th className="text-right py-2 px-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {data.transactions.map((t) => (
              <tr key={t.session_id} className="border-b border-zinc-100">
                <td className="py-2 px-2 max-w-[200px] truncate font-medium">{t.user_email}</td>
                <td className="py-2 px-2 text-xs">{t.tier}</td>
                <td className="text-right py-2 px-2 font-mono">{fmtUsd(t.amount)}</td>
                <td className="py-2 px-2">
                  <span className={`text-xs font-semibold ${
                    t.payment_status === "paid" ? "text-emerald-600" :
                    t.payment_status === "pending" ? "text-amber-600" :
                    "text-zinc-500"
                  }`}>{t.payment_status}</span>
                </td>
                <td className="text-right py-2 px-2 text-xs text-zinc-500">{fmtRelative(t.created_at)}</td>
              </tr>
            ))}
            {data.transactions.length === 0 && (
              <tr><td colSpan="5" className="text-center py-6 text-zinc-400">No payment transactions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BriefsSection({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-briefs">
      <div className="text-sm text-zinc-500 mb-3">{data.total} total briefs</div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {data.briefs.map((b) => (
          <div key={b.id} className="flex items-start gap-3 py-2 border-b border-zinc-100">
            <span className="text-violet-500 mt-0.5">📝</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-zinc-800 truncate">{b.product_name || b.product_slug}</div>
              <div className="text-xs text-zinc-500 truncate">{b.brief?.positioning || "—"}</div>
            </div>
            <div className="text-xs text-zinc-400 whitespace-nowrap">{fmtRelative(b.created_at)}</div>
          </div>
        ))}
        {data.briefs.length === 0 && (
          <div className="text-center py-6 text-zinc-400 text-sm">No briefs saved yet</div>
        )}
      </div>
    </div>
  );
}

function ActivitySection({ data }) {
  if (!data) return null;
  const iconFor = (t) => ({
    signup: "👤", opportunity: "💡", payment: "💳", brief: "📝",
  })[t] || "•";
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-activity">
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {data.events.map((e) => (
          <div key={`${e.type}-${e.at}-${(e.label || "").slice(0,40)}`} className="flex items-start gap-3 py-1.5 border-b border-zinc-100 text-sm">
            <span className="text-lg leading-tight">{iconFor(e.type)}</span>
            <div className="flex-1 min-w-0">
              <div className="text-zinc-800 truncate">{e.label}</div>
              <div className="text-[11px] text-zinc-400">{fmtRelative(e.at)} · {fmtDate(e.at)}</div>
            </div>
          </div>
        ))}
        {data.events.length === 0 && (
          <div className="text-center py-6 text-zinc-400">No recent activity</div>
        )}
      </div>
    </div>
  );
}

function AuditSection({ data }) {
  if (!data) return null;
  const statusPill = (s) => {
    const map = {
      ok: "bg-emerald-100 text-emerald-700",
      started: "bg-blue-100 text-blue-700",
      error: "bg-rose-100 text-rose-700",
      failed: "bg-rose-100 text-rose-700",
    };
    return map[s] || "bg-zinc-100 text-zinc-600";
  };
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-audit">
      <div className="text-sm text-zinc-500 mb-3">{data.total} total audit events · last {data.events.length} shown</div>
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {data.events.map((e, idx) => (
          <div key={`${e.at}-${e.action}-${idx}`} className="flex items-center gap-3 py-1.5 border-b border-zinc-100 text-xs">
            <span className={`px-2 py-0.5 rounded font-bold uppercase tracking-wider ${statusPill(e.status)}`}>{e.status}</span>
            <span className="font-mono text-zinc-700 font-semibold">{e.action}</span>
            <span className="text-zinc-400 truncate flex-1">
              {e.meta && Object.keys(e.meta).length > 0
                ? Object.entries(e.meta).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === "string" ? v.slice(0,40) : JSON.stringify(v).slice(0,40)}`).join(" · ")
                : "—"}
            </span>
            <span className="text-zinc-400 whitespace-nowrap">{fmtRelative(e.at)}</span>
          </div>
        ))}
        {data.events.length === 0 && (
          <div className="text-center py-6 text-zinc-400 text-sm">No admin actions logged yet — trigger a manual control above to see entries appear here.</div>
        )}
      </div>
    </div>
  );
}

function ControlsSection({ refreshAll }) {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const audit = (action, status, meta) => {
    // Best-effort fire-and-forget; never blocks the UI.
    adminAxios.post(`${API}/admin/audit`, { action, status, meta }).catch((e) => {
      console.warn("audit log failed", e);
    });
  };

  const run = async (label, action) => {
    setBusy(label); setMsg(null);
    audit(label, "started", null);
    try {
      const r = await action();
      setMsg({ ok: true, text: r });
      audit(label, "ok", { result: r });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setMsg({ ok: false, text: detail });
      audit(label, "error", { error: String(detail).slice(0, 200) });
    } finally {
      setBusy(null);
      setTimeout(refreshAll, 1500);
    }
  };

  const triggerScan = (scan_type, label) => () => run(label, async () => {
    const r = await adminAxios.post(`${API}/scan/start`, { scan_type });
    return `${label} started · ${r.data.status || "ok"}`;
  });

  const triggerAnalyze = () => run("analyze", async () => {
    const r = await adminAxios.post(`${API}/analyze/sync`, {});
    return `Analyze complete · ${r.data.opportunities_created ?? r.data.extracted ?? "ok"} opps`;
  });

  const triggerTrends = () => run("trends", async () => {
    const r = await adminAxios.post(`${API}/trends/refresh`);
    return `Trends refreshed · ${r.data.status || "ok"}`;
  });

  const triggerCleanup = () => {
    if (!window.confirm("Delete opportunities older than 90 days with score < 30?")) return;
    return run("cleanup", async () => {
      const r = await adminAxios.post(`${API}/admin/controls/cleanup-opportunities`, {
        older_than_days: 90, min_score: 30,
      });
      return `Deleted ${r.data.deleted} stale opportunities`;
    });
  };

  const triggerDiscover = () => run("discover", async () => {
    const r = await adminAxios.post(`${API}/trending/discover`, {});
    return `Discovered ${r.data.added} new topics · board now ${r.data.board_total?.toLocaleString()}`;
  });

  const triggerVerify = () => run("verify", async () => {
    const r = await adminAxios.post(`${API}/trending/verify-whitespace?limit=150`);
    return `SERP-verified ${r.data.checked} topics · ${r.data.open} confirmed whitespace`;
  });

  const triggerRefreshVolumes = () => run("volumes", async () => {
    const r = await adminAxios.post(`${API}/trending/topics-board/refresh-volumes`);
    if (r.data.started === false) return r.data.message || "A volume refresh is already running";
    return `Volume refresh started for ${r.data.topics?.toLocaleString()} topics · runs in background (~1–2 min)`;
  });

  const triggerRecuratePains = () => run("recurate", async () => {
    const r = await adminAxios.post(`${API}/pain-points/recurate`);
    if (r.data.started === false) return r.data.message || "Curation already running";
    return "Pain Points re-curation started · runs in background (~15 min)";
  });

  const triggerRecomputeVerdicts = () => run("verdicts", async () => {
    const r = await adminAxios.post(`${API}/trending/topics-board/recompute-verdicts`);
    const d = r.data?.dist || {};
    return `Verdicts recomputed · ${(r.data?.updated ?? 0).toLocaleString()} topics · emerging ${(d.emerging ?? 0).toLocaleString()}, saturated ${(d.saturated ?? 0).toLocaleString()}`;
  });

  const triggerRefreshScores = () => run("scores", async () => {
    const r = await adminAxios.post(`${API}/pain-points/refresh-scores`);
    if (r.data.started === false) return r.data.message || "Score refresh already running";
    return "Reddit engagement refresh started · pulls real upvotes/comments then re-curates (~30 min)";
  });

  const triggerRebuildReports = () => run("reports", async () => {
    const r = await adminAxios.post(`${API}/reports/weekly/rebuild`);
    if (r.data?.started === false) return r.data.message || "A rebuild is already running";
    return r.data?.message || "Weekly reports rebuilding in background · refresh the Reports page shortly";
  });

  const btn = "px-4 py-2.5 rounded-xl font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-zinc-200 p-5" data-testid="admin-controls">
      <div className="flex flex-wrap gap-3">
        <button data-testid="ctrl-premium-scan" disabled={!!busy} onClick={triggerScan("apify_full", "Premium Apify scan")}
          className={`${btn} bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700`}>
          {busy === "Premium Apify scan" ? "Starting…" : "🚀 Premium Apify scan"}
        </button>
        <button data-testid="ctrl-full-scan" disabled={!!busy} onClick={triggerScan("full", "Full scan")}
          className={`${btn} bg-zinc-800 text-white hover:bg-zinc-900`}>
          {busy === "Full scan" ? "Starting…" : "Full RSS scan"}
        </button>
        <button data-testid="ctrl-analyze" disabled={!!busy} onClick={triggerAnalyze}
          className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>
          {busy === "analyze" ? "Analyzing…" : "🤖 Re-run AI analyzer"}
        </button>
        <button data-testid="ctrl-trends" disabled={!!busy} onClick={triggerTrends}
          className={`${btn} bg-amber-500 text-white hover:bg-amber-600`}>
          {busy === "trends" ? "Refreshing…" : "📈 Refresh Google Trends"}
        </button>
        <button data-testid="ctrl-discover" disabled={!!busy} onClick={triggerDiscover}
          className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>
          {busy === "discover" ? "Discovering…" : "🔍 Discover new topics"}
        </button>
        <button data-testid="ctrl-verify" disabled={!!busy} onClick={triggerVerify}
          className={`${btn} bg-violet-600 text-white hover:bg-violet-700`}>
          {busy === "verify" ? "Verifying…" : "💎 Verify whitespace (SERP)"}
        </button>
        <button data-testid="ctrl-recompute-verdicts" disabled={!!busy} onClick={triggerRecomputeVerdicts}
          className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}>
          {busy === "verdicts" ? "Recomputing…" : "⚖️ Recompute verdicts now"}
        </button>
        <button data-testid="ctrl-refresh-volumes" disabled={!!busy} onClick={triggerRefreshVolumes}
          className={`${btn} bg-teal-600 text-white hover:bg-teal-700`}>
          {busy === "volumes" ? "Refreshing…" : "🔄 Refresh volumes now"}
        </button>
        <button data-testid="ctrl-recurate-pains" disabled={!!busy} onClick={triggerRecuratePains}
          className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}>
          {busy === "recurate" ? "Curating…" : "🧠 Re-curate Pain Points"}
        </button>
        <button data-testid="ctrl-refresh-scores" disabled={!!busy} onClick={triggerRefreshScores}
          className={`${btn} bg-orange-600 text-white hover:bg-orange-700`}>
          {busy === "scores" ? "Refreshing…" : "🔥 Refresh Reddit engagement"}
        </button>
        <button data-testid="ctrl-rebuild-reports" disabled={!!busy} onClick={triggerRebuildReports}
          className={`${btn} bg-fuchsia-600 text-white hover:bg-fuchsia-700`}>
          {busy === "reports" ? "Rebuilding…" : "🗞 Rebuild weekly reports"}
        </button>
        <button data-testid="ctrl-cleanup" disabled={!!busy} onClick={triggerCleanup}
          className={`${btn} bg-rose-600 text-white hover:bg-rose-700`}>
          {busy === "cleanup" ? "Cleaning…" : "🗑 Cleanup stale opps"}
        </button>
        <button data-testid="ctrl-refresh-all" disabled={!!busy} onClick={refreshAll}
          className={`${btn} bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50`}>
          ↻ Refresh data
        </button>
      </div>
      {msg && (
        <div className={`mt-4 px-4 py-3 rounded-lg text-sm ${msg.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid="admin-ctrl-msg">
          {msg.text}
        </div>
      )}
    </div>
  );
}

// ---------- Main Dashboard ----------
const HELP_TITLE = { advisor: "Advisor", discover: "Discover", evaluate: "Evaluate", signals: "Signals", ai_pulse: "AI Pulse", trending: "Trending", meta: "Meta Trends", country: "Best Country", briefs: "My Briefs", guide: "Guide (tour/help)" };

function HelpDigest({ data }) {
  const items = (data && data.items) || [];
  if (!items.length) return null;
  const byWeek = [...items].sort((a, b) => (b.clicks_7d || 0) - (a.clicks_7d || 0));
  const top = byWeek[0];
  const hasWeek = (top.clicks_7d || 0) > 0;
  const lead = hasWeek ? top : items[0];
  const label = HELP_TITLE[lead.feature] || lead.feature;
  return (
    <div className="mt-6 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 flex items-center gap-3" data-testid="admin-help-digest">
      <span className="text-base">🧭</span>
      <div className="text-sm text-zinc-700">
        <b className="text-zinc-900">Most-confusing screen {hasWeek ? "this week" : "(all time)"}:</b>{" "}
        <span className="font-semibold text-violet-700">{label}</span>{" "}
        <span className="text-zinc-500">— {hasWeek ? `${top.clicks_7d} help clicks in 7 days` : `${lead.clicks} help clicks total`} · {data.total} total help clicks across all screens</span>
      </div>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [data, setData] = useState({
    overview: null, apify: null, scraping: null, ai: null,
    users: null, payments: null, briefs: null, activity: null, audit: null, help: null,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [overview, apify, scraping, ai, users, payments, briefs, activity, audit, help] = await Promise.all([
        adminAxios.get(`${API}/admin/overview`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/apify-usage`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/scraping-health`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/ai-health`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/users`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/payments`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/briefs`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/activity`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/admin/audit?limit=50`).then(r => r.data).catch(() => null),
        adminAxios.get(`${API}/analytics/help-clicks`).then(r => r.data).catch(() => null),
      ]);
      // If a 401 happened anywhere, logout.
      if (!overview) {
        // Verify token to determine if it's auth or other issue
        try { await adminAxios.get(`${API}/admin/verify`); }
        catch { onLogout(); return; }
      }
      setData({ overview, apify, scraping, ai, users, payments, briefs, activity, audit, help });
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-violet-50/30 to-blue-50/30">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { window.history.pushState({}, "", "/"); window.location.reload(); }} className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 text-white font-bold flex items-center justify-center hover:scale-105 transition" title="Back to app" data-testid="admin-back-to-app">
              P
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-violet-600 font-bold">Prime Pillar</div>
              <h1 className="text-lg font-bold text-zinc-900">Admin Console</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 hidden sm:block">{loading ? "Loading…" : "Live data"}</span>
            <button onClick={loadAll} disabled={loading} className="text-sm px-3 py-1.5 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50" data-testid="admin-refresh">
              ↻ Refresh
            </button>
            <button onClick={onLogout} className="text-sm px-3 py-1.5 rounded-lg bg-zinc-900 text-white hover:bg-zinc-800" data-testid="admin-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-16">
        {err && <div className="mt-6 px-4 py-3 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">{err}</div>}

        <HelpDigest data={data.help} />

        <Section title="Overview" subtitle="Top-line metrics across the entire Prime Pillar pipeline.">
          <OverviewSection data={data.overview} apify={data.apify} />
        </Section>

        <Section title="Manual controls" subtitle="Trigger scans, AI runs, and maintenance jobs.">
          <ControlsSection refreshAll={loadAll} />
        </Section>

        <Section title="Scraping health" subtitle="Per-source raw signal counts and recent scan log.">
          <ScrapingHealthSection data={data.scraping} />
        </Section>

        <Section title="AI health" subtitle="Claude LLM probe status, score distribution, deep-dive usage.">
          <AiHealthSection data={data.ai} />
        </Section>

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <Section title="Users" subtitle="Newest first.">
            <UsersSection data={data.users} />
          </Section>
          <Section title="Payments" subtitle="Stripe transactions and revenue rollups.">
            <PaymentsSection data={data.payments} />
          </Section>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <Section title="Recent activity" subtitle="Unified timeline of signups, opps, briefs, payments.">
            <ActivitySection data={data.activity} />
          </Section>
          <Section title="Saved MVP briefs" subtitle="Built from G2 pain index.">
            <BriefsSection data={data.briefs} />
          </Section>
        </div>

        <Section title="Admin audit log" subtitle="Every manual control invocation, with status + payload. Cleared only if you wipe the collection.">
          <AuditSection data={data.audit} />
        </Section>

        <p className="mt-12 text-center text-xs text-zinc-400">Prime Pillar Admin Console · session auto-expires in 12 hours.</p>
      </main>
    </div>
  );
}

// ---------- Root component (handles login flow) ----------
export default function AdminRoot() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(ADMIN_TOKEN_KEY));

  // Verify token on mount — if expired, clear.
  useEffect(() => {
    if (!authed) return;
    adminAxios.get(`${API}/admin/verify`).catch(() => {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAuthed(false);
    });
  }, [authed]);

  const onLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onAuth={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={onLogout} />;
}
